(function() {
  const ACCESSORY_HINTS = new Set([
    "cage",
    "strap",
    "shield",
    "screen",
    "protector",
    "case",
    "bag",
    "plate",
    "mount",
    "tripod",
    "grip",
    "filter",
    "hood",
    "adapter",
    "rig",
    "backdrop",
    "paper"
  ]);
  const CAMERA_HINT_PHRASES = [
    "mirrorless camera",
    "dslr camera",
    "camera body",
    "body only",
    "camera with"
  ];
  const ROMAN_TO_INT = {
    i: 1,
    ii: 2,
    iii: 3,
    iv: 4,
    v: 5,
    vi: 6,
    vii: 7,
    viii: 8,
    ix: 9,
    x: 10
  };
  const INT_TO_ROMAN = Object.fromEntries(
    Object.entries(ROMAN_TO_INT).map(([roman, value]) => [String(value), roman])
  );
  const PREVIEW_LIMIT = 3;
  const PREVIEW_CANDIDATE_LIMIT = 30;
  const SEARCH_PAGE_SIZE = 24;
  const PREVIEW_OVERRIDE_WINDOW = 1200;

  function collapseSpaces(text) {
    return String(text || "").replace(/\s+/g, " ").trim();
  }

  function debounce(fn, delay) {
    let timer = null;
    return function debounced(...args) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function splitCompactDecimalRanges(text) {
    return text.replace(/(^|[^0-9])(\d+\.\d)(\d+\.\d)(?!\d)/g, (match, prefix, first, second) => {
      return `${prefix}${first} ${second}`;
    });
  }

  function normalizeBaseTokens(text) {
    let value = String(text || "").toLowerCase();
    value = splitCompactDecimalRanges(value);
    value = value.replace(/f\s*\/\s*(\d+(?:\.\d+)?)/g, "f$1");
    value = value.replace(/[^a-z0-9.\-\/\s]/g, " ");
    value = value.replace(/[\/\-]+/g, " ");
    return collapseSpaces(value);
  }

  function extractNumericRangeAliases(text) {
    const aliases = new Set();
    let match;
    const hyphenated = /(^|[^0-9])(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)(?:\s*mm)?(?!\d)/g;
    while ((match = hyphenated.exec(text)) !== null) {
      const first = match[2];
      const second = match[3];
      aliases.add(`${first}-${second}`);
      aliases.add(`${first} ${second}`);
      aliases.add(`${first}-${second}mm`);
      aliases.add(`${first} ${second}mm`);
    }

    const spaced = /(^|[^0-9])(\d{1,3})\s+(\d{2,3})(?:\s*mm)?(?!\d)/g;
    while ((match = spaced.exec(text)) !== null) {
      const first = match[2];
      const second = match[3];
      aliases.add(`${first}-${second}`);
      aliases.add(`${first} ${second}`);
      aliases.add(`${first}-${second}mm`);
      aliases.add(`${first} ${second}mm`);
    }

    return aliases;
  }

  function extractDimensionAliases(text) {
    const aliases = new Set();
    const work = splitCompactDecimalRanges(String(text || "").toLowerCase()).replace(/×/g, "x");
    const regex = /(^|[^0-9])(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)(?:\s*(?:'|"|ft|feet|in|inch|inches))?(?!\d)/g;
    let match;
    while ((match = regex.exec(work)) !== null) {
      const first = match[2];
      const second = match[3];
      aliases.add(`${first}x${second}`);
      aliases.add(`${first} x ${second}`);
    }
    return aliases;
  }

  function extractApertureAliases(text) {
    const aliases = new Set();
    const work = splitCompactDecimalRanges(String(text || "").toLowerCase());
    let match;
    const rangedF = /f\s*\/?\s*(\d+(?:\.\d+)?)\s*(?:-|to|\s)\s*(\d+(?:\.\d+)?)/g;
    while ((match = rangedF.exec(work)) !== null) {
      const first = match[1];
      const second = match[2];
      aliases.add(`f${first}-${second}`);
      aliases.add(`f/${first}-${second}`);
      aliases.add(`f ${first} ${second}`);
      aliases.add(`${first}-${second}`);
      aliases.add(`${first} ${second}`);
      aliases.add(`f${first}`);
      aliases.add(first);
      aliases.add(second);
    }

    const rangedNoF = /(^|[^0-9])(\d+\.\d+)\s*-\s*(\d+\.\d+)(?!\d)/g;
    while ((match = rangedNoF.exec(work)) !== null) {
      const first = match[2];
      const second = match[3];
      aliases.add(`f${first}-${second}`);
      aliases.add(`${first}-${second}`);
      aliases.add(`${first} ${second}`);
      aliases.add(first);
      aliases.add(second);
    }

    const singleF = /f\s*\/?\s*(\d+(?:\.\d+)?)/g;
    while ((match = singleF.exec(work)) !== null) {
      const value = match[1];
      aliases.add(`f${value}`);
      aliases.add(`f/${value}`);
      aliases.add(value);
    }

    return aliases;
  }

  function isAlphaToken(token) {
    return /^[a-z]+$/.test(token);
  }

  function isNumericishToken(token) {
    return /^\d+[a-z]?$/.test(token);
  }

  function isShortAlphaToken(token) {
    return isAlphaToken(token) && token.length <= 3;
  }

  function isAlnumCodeToken(token) {
    return /^[a-z0-9]+$/.test(token) && /[a-z]/.test(token) && /\d/.test(token);
  }

  function splitAlnumBoundaries(token) {
    return token.replace(/([a-z])(\d)/g, "$1 $2").replace(/(\d)([a-z])/g, "$1 $2");
  }

  function extractAlnumCodeAliases(text) {
    const aliases = new Set();
    const tokens = normalizeBaseTokens(text).split(" ").filter(Boolean);

    tokens.forEach((token) => {
      if (isAlnumCodeToken(token)) {
        const splitForm = collapseSpaces(splitAlnumBoundaries(token));
        const compactForm = splitForm.replace(/\s+/g, "");
        if (splitForm) {
          aliases.add(splitForm);
        }
        if (compactForm) {
          aliases.add(compactForm);
        }
      }
    });

    for (let index = 0; index < tokens.length - 1; index += 1) {
      const left = tokens[index];
      const right = tokens[index + 1];
      if (
        (isShortAlphaToken(left) && isNumericishToken(right)) ||
        (isNumericishToken(left) && isShortAlphaToken(right))
      ) {
        const joined = `${left}${right}`;
        if (isAlnumCodeToken(joined)) {
          aliases.add(joined);
          aliases.add(`${left} ${right}`);
        }
      }
    }

    String(text || "")
      .toLowerCase()
      .match(/\b[a-z]{1,6}\s*-\s*[a-z0-9]{1,12}\b/g)
      ?.forEach((match) => {
        const parts = match.split("-").map((part) => collapseSpaces(part)).filter(Boolean);
        if (parts.length !== 2) {
          return;
        }
        const [left, right] = parts;
        const hyphenated = `${left}-${right}`;
        const split = `${left} ${right}`;
        const compact = `${left}${right}`;
        aliases.add(hyphenated);
        aliases.add(split);
        if (isAlnumCodeToken(compact)) {
          aliases.add(compact);
        }
      });

    return aliases;
  }

  function parseVersionValue(value) {
    const normalized = String(value || "").toLowerCase().trim();
    if (/^\d+$/.test(normalized)) {
      const number = parseInt(normalized, 10);
      return number >= 1 && number <= 10 ? number : null;
    }
    return ROMAN_TO_INT[normalized] || null;
  }

  function addModelGenerationAliases(target, model, versionNumber) {
    if (/^f\d+[a-z]?$/.test(model) || /^\d+mm$/.test(model)) {
      return;
    }

    const roman = INT_TO_ROMAN[String(versionNumber)];
    target.add(`${model} ${versionNumber}`);
    target.add(`${model} mark ${versionNumber}`);
    target.add(`${model} mk ${versionNumber}`);
    target.add(`${model} mk${versionNumber}`);

    if (roman) {
      target.add(`${model} ${roman}`);
      target.add(`${model} mark ${roman}`);
      target.add(`${model} mk ${roman}`);
      target.add(`${model} mk${roman}`);
    }
  }

  function extractVersionAliases(text) {
    const aliases = new Set();
    const work = normalizeBaseTokens(text);
    let match;

    const vRegex = /\bv\s*(\d+)(?:\.0)?\b/g;
    while ((match = vRegex.exec(work)) !== null) {
      const versionNumber = parseInt(match[1], 10);
      if (versionNumber >= 1 && versionNumber <= 20) {
        aliases.add(`v${versionNumber}`);
        aliases.add(`v${versionNumber}.0`);
      }
    }

    const mkRegex = /\b(?:mark|mk)\s*([ivx]+|\d+)(?!\.\d)\b/g;
    while ((match = mkRegex.exec(work)) !== null) {
      const versionNumber = parseVersionValue(match[1]);
      if (!versionNumber) {
        continue;
      }
      const roman = INT_TO_ROMAN[String(versionNumber)];
      if (roman) {
        aliases.add(`mark ${roman}`);
        aliases.add(`mk ${roman}`);
        aliases.add(`mk${roman}`);
      }
      aliases.add(`mark ${versionNumber}`);
      aliases.add(`mk ${versionNumber}`);
      aliases.add(`mk${versionNumber}`);
    }

    const compactMkRegex = /\bmk([ivx]+|\d+)(?!\.\d)\b/g;
    while ((match = compactMkRegex.exec(work)) !== null) {
      const versionNumber = parseVersionValue(match[1]);
      if (!versionNumber) {
        continue;
      }
      const roman = INT_TO_ROMAN[String(versionNumber)];
      if (roman) {
        aliases.add(`mark ${roman}`);
        aliases.add(`mk ${roman}`);
        aliases.add(`mk${roman}`);
      }
      aliases.add(`mark ${versionNumber}`);
      aliases.add(`mk ${versionNumber}`);
      aliases.add(`mk${versionNumber}`);
    }

    const modelRegex = /\b([a-z0-9]+)\s+(?:(?:mark|mk)\s*)?([ivx]+|\d+)(?!\.\d)\b/g;
    while ((match = modelRegex.exec(work)) !== null) {
      const model = match[1];
      const versionNumber = parseVersionValue(match[2]);
      if (!isAlnumCodeToken(model) || !versionNumber) {
        continue;
      }
      addModelGenerationAliases(aliases, model, versionNumber);
    }

    const splitModelRegex = /\b([a-z]{1,6})\s+(\d{1,4}[a-z]?)\s+(?:(?:mark|mk)\s*)?([ivx]+|\d+)(?!\.\d)\b/g;
    while ((match = splitModelRegex.exec(work)) !== null) {
      const model = `${match[1]}${match[2]}`;
      const versionNumber = parseVersionValue(match[3]);
      if (!isAlnumCodeToken(model) || !versionNumber) {
        continue;
      }
      addModelGenerationAliases(aliases, model, versionNumber);
    }

    const numberLeadRegex = /(?:(?<=^)|(?<=\s))(\d{1,4})\s+([a-z]{1,3})\s+(?:(?:mark|mk)\s*)?([ivx]+|\d+)(?!\.\d)\b/g;
    while ((match = numberLeadRegex.exec(work)) !== null) {
      const model = `${match[1]}${match[2]}`;
      const versionNumber = parseVersionValue(match[3]);
      if (!isAlnumCodeToken(model) || !versionNumber) {
        continue;
      }
      addModelGenerationAliases(aliases, model, versionNumber);
    }

    const compactModelMkRegex = /\b([a-z0-9]+)\s+mk([ivx]+|\d+)(?!\.\d)\b/g;
    while ((match = compactModelMkRegex.exec(work)) !== null) {
      const model = match[1];
      const versionNumber = parseVersionValue(match[2]);
      if (!isAlnumCodeToken(model) || !versionNumber) {
        continue;
      }
      addModelGenerationAliases(aliases, model, versionNumber);
    }

    return aliases;
  }

  function buildSearchAliases(text) {
    const base = normalizeBaseTokens(text);
    const aliases = new Set();
    aliases.add(base);
    [
      extractAlnumCodeAliases(text),
      extractAlnumCodeAliases(base),
      extractNumericRangeAliases(base),
      extractDimensionAliases(text),
      extractDimensionAliases(base),
      extractApertureAliases(text),
      extractApertureAliases(base),
      extractVersionAliases(text),
      extractVersionAliases(base)
    ].forEach((collection) => {
      collection.forEach((alias) => aliases.add(alias));
    });

    const cleaned = [];
    aliases.forEach((alias) => {
      const normalized = normalizeBaseTokens(alias);
      if (normalized && !cleaned.includes(normalized)) {
        cleaned.push(normalized);
      }
    });
    return cleaned;
  }

  function buildSearchText(text) {
    const tokens = [];
    buildSearchAliases(text).forEach((alias) => {
      alias.split(" ").forEach((token) => {
        if (!token) {
          return;
        }
        let normalized = token;
        if (/^f\d+\.\d+$/.test(token) || /^\d+\.\d+$/.test(token)) {
          normalized = token.replace(/\./g, "d");
        }
        if (!tokens.includes(normalized)) {
          tokens.push(normalized);
        }
      });
    });
    return collapseSpaces(tokens.join(" "));
  }

  function normalizeModelQuery(text) {
    let value = String(text || "").toLowerCase();
    value = value.replace(/[^a-z0-9.\-\/\s]/g, " ");
    value = value.replace(/[\/\-]+/g, " ");
    return collapseSpaces(value);
  }

  function isModelIntentQuery(query) {
    const lowered = String(query || "").toLowerCase();
    for (const term of ACCESSORY_HINTS) {
      if (new RegExp(`\\b${term}\\b`).test(lowered)) {
        return false;
      }
    }
    return (
      /\b[a-z]+\d+[a-z]*\b/.test(lowered) ||
      /\b\d+[a-z]+\b/.test(lowered) ||
      /\b(?:mark|mk)\s*(?:\d+|[ivx]+)\b/.test(lowered) ||
      /\b[ivx]{2,}\b/.test(lowered)
    );
  }

  function extractModelTokens(query) {
    return Array.from(
      new Set((String(query || "").toLowerCase().match(/\b(?:[a-z]+\d+[a-z]*|\d+[a-z]+)\b/g) || []))
    );
  }

  function cameraIntentTitleBoost(title) {
    const lowered = String(title || "").toLowerCase();
    let boost = 0;

    if (CAMERA_HINT_PHRASES.some((phrase) => lowered.includes(phrase))) {
      boost += 50;
    }

    for (const term of ACCESSORY_HINTS) {
      if (new RegExp(`\\b${term}\\b`).test(lowered)) {
        boost -= 60;
      }
    }

    return boost;
  }

  function modelTokenMatchScore(document, modelTokens) {
    if (!modelTokens.length) {
      return 0;
    }

    const haystacks = [];
    ["search_aliases", "search_text", "product_title"].forEach((key) => {
      const value = document[key];
      if (Array.isArray(value)) {
        value.forEach((item) => haystacks.push(String(item).toLowerCase()));
      } else if (typeof value === "string") {
        haystacks.push(value.toLowerCase());
      }
    });

    let score = 0;
    modelTokens.forEach((token) => {
      if (
        haystacks.some((text) =>
          new RegExp(`(^|[^a-z0-9])${token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`).test(text)
        )
      ) {
        score += 1;
      }
    });
    return score;
  }

  function extractHitDocument(hit) {
    if (!hit || typeof hit !== "object") {
      return {};
    }
    return hit.document && typeof hit.document === "object" ? hit.document : hit;
  }

  function extractHitId(hit) {
    if (!hit || typeof hit !== "object") {
      return null;
    }
    return hit.id || hit.objectID || (hit.document && hit.document.id) || null;
  }

  function extractHitTitle(hit) {
    const document = extractHitDocument(hit);
    return document.product_title || document.title || "";
  }

  function rootWithTrailingSlash(rootUrl) {
    const value = String(rootUrl || "/");
    return value.endsWith("/") ? value : `${value}/`;
  }

  function buildStoreUrl(rootUrl, path) {
    return `${rootWithTrailingSlash(rootUrl)}${String(path || "").replace(/^\/+/, "")}`;
  }

  function buildTypesenseEndpoint(config) {
    const base = `${config.protocol}://${config.host}${config.port ? `:${config.port}` : ""}`;
    return `${base}/collections/${config.collection}/documents/search`;
  }

  function prepareTypesenseHits(hits, rawQuery) {
    const modelTokens = extractModelTokens(buildSearchText(rawQuery) || rawQuery);
    const prepared = hits.map((hit, index) => {
      const document = extractHitDocument(hit);
      const title = extractHitTitle(hit);
      return {
        id: String(extractHitId(hit) || "").replace(/^gid:\/\/shopify\/Product\//, ""),
        title,
        path: document.path || "",
        imageUrl: document.image_url || "",
        price: document.price,
        modelScore: modelTokenMatchScore(document, modelTokens),
        textMatch: hit.text_match || hit._text_match || 0,
        position: index
      };
    });

    if (isModelIntentQuery(rawQuery)) {
      prepared.sort((left, right) => {
        if (left.modelScore !== right.modelScore) {
          return right.modelScore - left.modelScore;
        }
        const leftBoost = cameraIntentTitleBoost(left.title);
        const rightBoost = cameraIntentTitleBoost(right.title);
        if (leftBoost !== rightBoost) {
          return rightBoost - leftBoost;
        }
        if (left.textMatch !== right.textMatch) {
          return right.textMatch - left.textMatch;
        }
        return left.position - right.position;
      });
    }

    return prepared;
  }

  async function fetchTypesenseResults(config, query, perPage, page) {
    const modelIntent = isModelIntentQuery(query);
    const normalizedQuery = modelIntent ? normalizeModelQuery(query) || query : buildSearchText(query) || query;
    const params = new URLSearchParams({
      q: normalizedQuery,
      query_by: "variant_skus,search_aliases,search_text,product_title",
      query_by_weights: "8,5,3,2",
      sort_by: "_text_match:desc,popularity_score:desc",
      per_page: String(perPage),
      page: String(page || 1)
    });

    if (modelIntent) {
      params.set("drop_tokens_threshold", "0");
      params.set("enable_typos_for_alpha_numerical_tokens", "false");
    } else {
      params.set("drop_tokens_threshold", "1");
      params.set("enable_typos_for_alpha_numerical_tokens", "true");
    }

    const response = await fetch(`${buildTypesenseEndpoint(config)}?${params.toString()}`, {
      method: "GET",
      headers: {
        "X-TYPESENSE-API-KEY": config.searchOnlyKey
      }
    });

    if (!response.ok) {
      throw new Error(`Typesense request failed with ${response.status}`);
    }

    const result = await response.json();
    const hits = Array.isArray(result.hits) ? result.hits : [];
    return {
      hits,
      preparedHits: prepareTypesenseHits(hits, query),
      totalHits: result.found || result.out_of || hits.length
    };
  }

  function buildIdQuery(ids) {
    return ids.map((id) => `id:${id}`).join(" OR ");
  }

  async function fetchSectionHtml(url) {
    const response = await fetch(url, {
      method: "GET",
      credentials: "same-origin"
    });
    if (!response.ok) {
      throw new Error(`Section request failed with ${response.status}`);
    }
    return response.text();
  }

  function parseHtml(html) {
    return new DOMParser().parseFromString(html, "text/html");
  }

  function formatMoney(amount) {
    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount)) {
      return "";
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD"
      }).format(numericAmount);
    } catch (error) {
      return `$${numericAmount.toFixed(2)}`;
    }
  }

  function buildPreviewItemsMarkup(items) {
    return items
      .map((item) => {
        const productUrl = item.path || `${window.routes.searchUrl}?q=${encodeURIComponent(`id:${item.id}`)}&type=product`;
        const formattedPrice = item.price != null ? formatMoney(item.price) : "";
        const priceMarkup = formattedPrice
          ? `<span class="search-bar__item-price">${escapeHtml(formattedPrice)}</span>`
          : "";

        return `
          <a href="${escapeHtml(productUrl)}" class="search-bar__result-item">
            <div class="search-bar__image-container">
              <div class="aspect-ratio aspect-ratio--square">
                <img src="${escapeHtml(item.imageUrl || "https://cdn.shopify.com/s/files/1/0604/0515/3875/files/image_placeholder_2026_white.jpg?v=1770684625")}" alt="${escapeHtml(item.title)}">
              </div>
            </div>

            <div class="search-bar__item-info">
              <p class="search-bar__item-title">${escapeHtml(item.title)}</p>
              ${priceMarkup}
            </div>
          </a>
        `;
      })
      .join("");
  }

  function splitPreviewItems(items) {
    const used = [];
    const fresh = [];

    items.forEach((item) => {
      if (!item || !item.title) {
        return;
      }
      if (/^used\b/i.test(item.title)) {
        if (used.length < 3) {
          used.push(item);
        }
      } else if (fresh.length < 3) {
        fresh.push(item);
      }
    });

    return { fresh, used };
  }

  function selectPreviewItems(items) {
    const used = [];
    const fresh = [];

    items.forEach((item) => {
      if (!item || !item.id || !item.title) {
        return;
      }

      if (/^used\b/i.test(item.title)) {
        if (used.length < PREVIEW_LIMIT) {
          used.push(item);
        }
      } else if (fresh.length < PREVIEW_LIMIT) {
        fresh.push(item);
      }
    });

    return fresh.concat(used);
  }

  function buildDirectPreviewMarkup(items, query, texts) {
    const groups = splitPreviewItems(items);
    const sections = [];

    if (groups.fresh.length) {
      sections.push(`
        <p class="search-bar__result-category">${escapeHtml(texts.newProductsLabel)}</p>
        <div class="search-bar__result-products">
          ${buildPreviewItemsMarkup(groups.fresh)}
        </div>
      `);
    }

    if (groups.used.length) {
      sections.push(`
        <p class="search-bar__result-category">${escapeHtml(texts.usedProductsLabel)}</p>
        <div class="search-bar__result-products">
          ${buildPreviewItemsMarkup(groups.used)}
        </div>
      `);
    }

    return `
      <div class="search-bar__typesense-preview" data-typesense-query="${escapeHtml(query)}">
        ${sections.join("")}
        <button class="search-bar__view-all" type="submit">
          ${escapeHtml(texts.viewResultsLabel)}
        </button>
      </div>
    `.replace(
      '<button class="search-bar__view-all" type="submit">',
      `<button class="search-bar__view-all" type="submit" aria-label="${escapeHtml(`View all results for ${query}`)}">`
    );
  }

  async function fetchHydratedSearchSection(config, ids) {
    const idQuery = buildIdQuery(ids);
    const url = `${buildStoreUrl(config.shopifyRootUrl, "search")}?section_id=${encodeURIComponent(
      config.searchSectionId
    )}&type=product&q=${encodeURIComponent(idQuery)}`;
    const html = await fetchSectionHtml(url);
    const doc = parseHtml(html);
    return doc.querySelector('[data-section-type="collection"]');
  }

  function buildPaginationMarkup(totalHits, currentPage, perPage, texts) {
    const totalPages = Math.max(1, Math.ceil(totalHits / perPage));
    if (totalPages <= 1) {
      return "";
    }

    const url = new URL(window.location.href);
    const pageLink = (page) => {
      const nextUrl = new URL(url.toString());
      if (page <= 1) {
        nextUrl.searchParams.delete("page");
      } else {
        nextUrl.searchParams.set("page", String(page));
      }
      return nextUrl.pathname + nextUrl.search;
    };

    const navItems = [];
    for (let page = 1; page <= totalPages; page += 1) {
      if (page === currentPage) {
        navItems.push(`<span class="pagination__nav-item is-active">${page}</span>`);
      } else {
        navItems.push(`<a href="${escapeHtml(pageLink(page))}" data-page="${page}" class="pagination__nav-item link" title="${escapeHtml(
          `${texts.goToPageLabel} ${page}`
        )}">${page}</a>`);
      }
    }

    const previousMarkup =
      currentPage > 1
        ? `<a class="pagination__prev link" rel="prev" data-page="${currentPage - 1}" href="${escapeHtml(
            pageLink(currentPage - 1)
          )}">${escapeHtml(texts.previousPageLabel)}</a>`
        : "";
    const nextMarkup =
      currentPage < totalPages
        ? `<a class="pagination__next link" rel="next" data-page="${currentPage + 1}" href="${escapeHtml(
            pageLink(currentPage + 1)
          )}">${escapeHtml(texts.nextPageLabel)}</a>`
        : "";

    return `
      <div class="pagination">
        <div class="pagination__inner">
          ${previousMarkup}
          <div class="pagination__nav">${navItems.join("")}</div>
          <span class="pagination__page-count">${escapeHtml(
            texts.pageCountLabel
              .replace("__CURRENT__", String(currentPage))
              .replace("__PAGES__", String(totalPages))
          )}</span>
          ${nextMarkup}
        </div>
      </div>
    `;
  }

  function updateSearchCounts(section, query, totalHits, currentPage, perPage) {
    const offset = totalHits === 0 ? 0 : (currentPage - 1) * perPage + 1;
    const pageSize = Math.min(currentPage * perPage, totalHits);
    const totalText = totalHits === 1 ? "1 result" : `${totalHits} results`;
    const showingText = totalHits === 0 ? "0 results" : `Showing ${offset}-${pageSize} of ${totalHits}`;

    section.querySelectorAll(".collection__products-count-total").forEach((node) => {
      node.textContent = totalText;
    });
    section.querySelectorAll(".collection__showing-count").forEach((node) => {
      node.textContent = showingText;
    });
    section.querySelectorAll(".collection__mobile-active-filters-results").forEach((node) => {
      node.textContent = totalText;
    });
    section.querySelectorAll(".collection__title").forEach((node) => {
      node.textContent = `Products for "${query}"`;
    });
  }

  function hideNativeSearchControls(section) {
    [
      ".collection__toolbar-item--filter",
      ".collection__toolbar-item--sort",
      ".collection__mobile-filters-recap"
    ].forEach((selector) => {
      section.querySelectorAll(selector).forEach((node) => {
        node.style.display = "none";
      });
    });
  }

  function hasUnsupportedSearchParams(url) {
    for (const [key, value] of url.searchParams.entries()) {
      if (key.startsWith("filter.")) {
        return true;
      }
      if (key === "sort_by" && value) {
        return true;
      }
    }
    return false;
  }

  class HeaderTypesenseSearch {
    constructor(config) {
      this.config = config;
      this.wrapper = document.querySelector(".header__search-bar-wrapper");
      if (!this.wrapper) {
        return;
      }
      this.input = this.wrapper.querySelector('.search-bar__input[name="q"]');
      this.resultsInner = this.wrapper.querySelector(".search-bar__results-inner");
      this.searchBar = this.wrapper.querySelector(".search-bar");
      if (!this.input || !this.resultsInner || !this.searchBar) {
        return;
      }
      this.sequence = 0;
      this.latestMarkup = null;
      this.latestQuery = "";
      this.overrideObserver = null;
      this.overrideTimer = null;
      this.isApplyingMarkup = false;
      this.handleInput = debounce(this.handleInput.bind(this), 150);
      this.input.addEventListener("input", this.handleInput);
    }

    stopWatchingNativeUpdates() {
      window.clearTimeout(this.overrideTimer);
      if (this.overrideObserver) {
        this.overrideObserver.disconnect();
        this.overrideObserver = null;
      }
    }

    applyLatestMarkup() {
      if (!this.latestMarkup || this.input.value.trim() !== this.latestQuery) {
        return;
      }
      this.isApplyingMarkup = true;
      if (this.overrideObserver) {
        this.overrideObserver.disconnect();
      }
      this.resultsInner.innerHTML = this.latestMarkup;
      this.searchBar.classList.remove("is-loading");
      this.isApplyingMarkup = false;
    }

    startWatchingNativeUpdates(sequence) {
      this.stopWatchingNativeUpdates();
      if (typeof MutationObserver !== "function") {
        return;
      }

      this.overrideObserver = new MutationObserver(() => {
        if (this.isApplyingMarkup || sequence !== this.sequence) {
          return;
        }
        if (!this.latestMarkup || this.input.value.trim() !== this.latestQuery) {
          return;
        }

        const existingPreview = this.resultsInner.querySelector(".search-bar__typesense-preview");
        const existingQuery = existingPreview ? existingPreview.getAttribute("data-typesense-query") || "" : "";
        if (existingQuery === this.latestQuery) {
          return;
        }

        this.applyLatestMarkup();
        if (this.overrideObserver && sequence === this.sequence) {
          this.overrideObserver.observe(this.resultsInner, {
            childList: true,
            subtree: true
          });
        }
      });

      this.overrideObserver.observe(this.resultsInner, {
        childList: true,
        subtree: true
      });

      this.overrideTimer = window.setTimeout(() => {
        if (sequence === this.sequence) {
          this.stopWatchingNativeUpdates();
        }
      }, PREVIEW_OVERRIDE_WINDOW);
    }

    async handleInput() {
      const query = this.input.value.trim();
      this.sequence += 1;
      const sequence = this.sequence;
      this.latestQuery = query;
      this.latestMarkup = null;
      this.stopWatchingNativeUpdates();

      if (!query) {
        return;
      }

      try {
        const { preparedHits } = await fetchTypesenseResults(
          this.config.typesense,
          query,
          PREVIEW_CANDIDATE_LIMIT,
          1
        );
        if (sequence !== this.sequence) {
          return;
        }

        const previewItems = selectPreviewItems(preparedHits);
        if (!previewItems.length) {
          return;
        }
        if (sequence !== this.sequence) {
          return;
        }

        this.latestMarkup = buildDirectPreviewMarkup(previewItems, query, this.config.texts);
        this.applyLatestMarkup();
        this.startWatchingNativeUpdates(sequence);
      } catch (error) {
      }
    }
  }

  class SearchPageTypesense {
    constructor(config) {
      this.config = config;
      this.url = new URL(window.location.href);
      this.section = document.querySelector('[data-section-type="collection"]');
      this.query = this.url.searchParams.get("q");
      this.currentPage = Math.max(1, parseInt(this.url.searchParams.get("page") || "1", 10) || 1);

      if (window.theme.pageType !== "search" || !this.section || !this.query || hasUnsupportedSearchParams(this.url)) {
        return;
      }

      this.load();
    }

    async load() {
      try {
        const { preparedHits, totalHits } = await fetchTypesenseResults(
          this.config.typesense,
          this.query,
          SEARCH_PAGE_SIZE,
          this.currentPage
        );
        const ids = preparedHits.map((hit) => hit.id).filter(Boolean);
        if (!ids.length) {
          return;
        }

        const hydratedSection = await fetchHydratedSearchSection(this.config, ids);
        if (!hydratedSection) {
          return;
        }

        this.section.innerHTML = hydratedSection.innerHTML;
        hideNativeSearchControls(this.section);
        updateSearchCounts(this.section, this.query, totalHits, this.currentPage, SEARCH_PAGE_SIZE);

        const productList = this.section.querySelector(".product-list--collection");
        if (productList) {
          productList.setAttribute("data-typesense-results", "true");
        }

        const existingPagination = this.section.querySelector(".pagination");
        const paginationMarkup = buildPaginationMarkup(totalHits, this.currentPage, SEARCH_PAGE_SIZE, this.config.texts);
        if (existingPagination) {
          existingPagination.outerHTML = paginationMarkup;
        } else if (paginationMarkup) {
          const productListNode = this.section.querySelector(".product-list--collection");
          if (productListNode) {
            productListNode.insertAdjacentHTML("afterend", paginationMarkup);
          }
        }
      } catch (error) {
      }
    }
  }

  function init() {
    const configNode = document.querySelector("[data-typesense-search-config]");
    if (!configNode) {
      return;
    }

    let config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      return;
    }

    new HeaderTypesenseSearch(config);
    new SearchPageTypesense(config);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
